// much of what is in here comes from https://github.com/visgl/react-google-maps/blob/main/examples/drawing/src/types.ts

import React from "react";
import { formatDistanceToNowStrict, isValid } from "date-fns";
import { useParams } from "react-router-dom";

import RoutePath from "./RoutePath";
import { useMapPathZoom } from "./use-map-zoom";
import SidepanelHeader from "./ui/sidepanel-header";
import { calculateCrewStatus, CrewIcon, CrewPin } from "./ui/crew";
import { colors } from "./colors";
import { resolveRouteColorPalette } from "./routeColor";
import { useMap } from "@vis.gl/react-google-maps";
import useNavigateKeepSearchParams from "../../../hooks/useNavigateKeepSearchParams";

const seasonBadges = {
  snow: {
    label: "Snow",
    className: `${colors.blue.bgLight} ${colors.blue.text}`,
  },
  landscape: {
    label: "Landscape",
    className: `${colors.green.bgLight} ${colors.green.text}`,
  },
};

const ActiveRouteItem = ({ data, showSeasonBadge }) => {
  const navigate = useNavigateKeepSearchParams(["app_version"]);
  let { routeId } = useParams();
  let baseUrl = window.location.pathname.match(/(.*active-routes)\//)[1];

  const routeColor = data.route.color ?? "emerald";
  const seasonBadge = seasonBadges[data.route.season];
  const paused = data.status === "paused";
  const pausedDate = data.pausedAt ? new Date(data.pausedAt) : null;

  let bgColor = resolveRouteColorPalette(routeColor).bgLight;
  let routePathColor = resolveRouteColorPalette(routeColor).hex;
  let textColor = resolveRouteColorPalette(routeColor).text;

  let makeRouteActive = routeId === undefined || routeId === data._id;

  let jobsRemaining = data.route.jobs.reduce((acc, job) => {
    if (job.status === "upcoming") return acc;

    acc++;
    return acc;
  }, 0);

  let routeStatus = (
    <span
      className={`inline-flex items-center rounded-md ${bgColor} px-2 py-1 text-xs font-medium ${textColor} ring-1 ring-inset ring-red-600/10`}
    >
      {jobsRemaining} / {data.route.jobs.length}
    </span>
  );

  let routeClick = (e) => {
    e.preventDefault();
    navigate(`${baseUrl}/main/${data._id}`);
  };
  let mapItemClick = (path) => {
    routeClick(path.domEvent);
  };
  const [crewStatus] = calculateCrewStatus(
    data.operator.lastKnownLocation?.createdAt,
  );

  return (
    <li className="border-t border-gray-100 py-3 pr-4 pl-10 hover:bg-gray-50">
      <a
        href={`/admin/active-routes/main/${data._id}`}
        onClick={routeClick}
        className="hover:cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <span
            className={`size-2 shrink-0 rounded-full ${resolveRouteColorPalette(routeColor).bg}`}
          />
          <p className={`min-w-0 flex-1 truncate text-sm ${textColor}`}>
            {data.route.name}
          </p>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {showSeasonBadge && seasonBadge ? (
            <span
              className={`shrink-0 rounded-md px-1.5 py-0.5 text-xs font-medium ${seasonBadge.className}`}
            >
              {seasonBadge.label}
            </span>
          ) : null}
          <span
            className={`rounded-md px-2 py-1 text-xs font-medium ${paused ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}
          >
            {paused ? "Paused" : "Active"}
          </span>
          <time className="flex-none text-xs text-gray-700">{routeStatus}</time>
        </div>
        {paused ? (
          <p className="mt-1 ml-4 text-xs text-gray-500">
            {pausedDate && isValid(pausedDate)
              ? `Paused ${formatDistanceToNowStrict(pausedDate, { addSuffix: true })}`
              : "Pause time unavailable"}
          </p>
        ) : null}
      </a>
      {!paused && data.operator.lastKnownLocation ? (
        <CrewPin
          position={data.operator.lastKnownLocation}
          status={crewStatus}
          color={routeColor}
          initials={data.operator.firstName[0] + data.operator.lastName[0]}
        />
      ) : null}
      {data.route.drivingPath && (!paused || routeId === data._id) ? (
        <RoutePath
          pathClick={mapItemClick}
          path={data.route.drivingPath}
          color={routePathColor}
          isActive={paused ? false : makeRouteActive}
        />
      ) : null}
    </li>
  );
};

const CrewRouteGroup = ({ routes, showSeasonBadges }) => {
  const operator = routes[0].operator;
  const activeRoute =
    routes.find((route) => route.status !== "paused") ?? routes[0];
  const routeColor = activeRoute.route.color ?? "emerald";
  const [crewStatus] = calculateCrewStatus(
    operator.lastKnownLocation?.createdAt,
  );
  const navigate = useNavigateKeepSearchParams(["app_version"]);
  const baseUrl = window.location.pathname.match(/(.*active-routes)\//)[1];

  return (
    <li className="border-t border-gray-300 first:border-t-0">
      <button
        className="flex items-center gap-3 px-4 py-4"
        onClick={() => {
          navigate(`${baseUrl}/main/crew/${operator._id}`);
        }}
      >
        <CrewIcon
          lessSpace={true}
          color={routeColor}
          enabled={crewStatus === "active"}
          initials={operator.firstName[0] + operator.lastName[0]}
        />
        <h3 className="min-w-0 flex-1 truncate text-sm/6 font-semibold text-gray-900">
          {operator.firstName} {operator.lastName}
        </h3>
      </button>
      <ul role="list">
        {[...routes]
          .sort((first, second) => {
            const statusOrder =
              Number(first.status === "paused") -
              Number(second.status === "paused");
            if (statusOrder !== 0) return statusOrder;
            if (first.status !== "paused") return 0;

            return (
              (Date.parse(second.pausedAt) || 0) -
              (Date.parse(first.pausedAt) || 0)
            );
          })
          .map((route) => (
            <ActiveRouteItem
              key={route._id}
              data={route}
              showSeasonBadge={showSeasonBadges}
            />
          ))}
      </ul>
    </li>
  );
};

const ActiveRoutesMap = ({ activeRoutes, offJobStaff }) => {
  let routePaths = [];
  activeRoutes
    .filter((route) => route.status !== "paused")
    .map((route) => routePaths.push(route.route.drivingPath));

  useMapPathZoom(routePaths);

  return (
    <>
      <div className="px-6 pb-6">
        <div className="flex items-start justify-between">
          <SidepanelHeader>Active Crews</SidepanelHeader>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          See active and paused route details
        </p>
      </div>
      <div className="md-py-16">
        <ActiveRoutesList activeRoutes={activeRoutes} />
      </div>
      <div className="p-6 pb-2 border-t border-gray-300">
        <div className="flex items-start justify-between">
          <SidepanelHeader>Crew Members Between Routes</SidepanelHeader>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Clocked in crew not servicing a route
        </p>
      </div>
      <div className="md-py-16">
        <ul role="list" className="flex-1 divide-y divide-gray-100">
          {offJobStaff?.map((s, index) => (
            <li key={index} className="p-6 py-4">
              <OffJobStaff crew={s} />
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

const OffJobStaff = ({ crew }) => {
  const map = useMap();
  const navigate = useNavigateKeepSearchParams(["app_version"]);
  const baseUrl = window.location.pathname.match(/(.*active-routes)\//)[1];

  const [crewStatus] = calculateCrewStatus(crew.lastKnownLocation?.createdAt);

  const handleClick = () => {
    // Navigate to crew detail
    navigate(`${baseUrl}/main/crew/${crew._id}`);

    // Center map on crew location if available
    if (crew.lastKnownLocation) {
      let bounds = new google.maps.LatLngBounds();
      bounds.extend(crew.lastKnownLocation);
      map.fitBounds(bounds, 100);
      map.setZoom(15);
    }
  };

  return (
    <a
      className="flex items-center gap-x-3 cursor-pointer"
      onClick={handleClick}
    >
      <CrewIcon
        lessSpace={true}
        color="gray"
        enabled={crewStatus === "active"}
        initials={crew.firstName[0] + crew.lastName[0]}
      />
      <h3
        className={`flex-auto truncate text-sm/6 font-semibold text-gray-600`}
      >
        {crew.firstName} {crew.lastName}
      </h3>
      <CrewPin
        position={crew.lastKnownLocation}
        color="gray"
        status={crewStatus}
        initials={crew.firstName[0] + crew.lastName[0]}
      ></CrewPin>
    </a>
  );
};

const ActiveRoutesList = ({ activeRoutes }) => {
  const showSeasonBadges =
    activeRoutes.some((route) => route.route.season === "snow") &&
    activeRoutes.some((route) => route.route.season === "landscape");
  const routesByOperator = new Map();
  activeRoutes.forEach((route) => {
    const operatorId = route.operator._id ?? route.operator.uid;
    routesByOperator.set(operatorId, [
      ...(routesByOperator.get(operatorId) ?? []),
      route,
    ]);
  });

  return (
    <ul role="list" className="flex-1 border-t border-gray-300">
      {routesByOperator.size == 0 ? (
        <li className="p-6">
          <h3 className="flex-auto truncate text-sm/6 text-gray-900">
            No active or paused routes right now
          </h3>
        </li>
      ) : (
        Array.from(routesByOperator.entries()).map(([operatorId, routes]) => (
          <CrewRouteGroup
            key={operatorId}
            routes={routes}
            showSeasonBadges={showSeasonBadges}
          />
        ))
      )}
    </ul>
  );
};

export default ActiveRoutesMap;
